import { List } from "client/Features/Hub/KnowledgeItems/CategoryOverview/List/List";
import {
	NoResults,
	BusyIndicator,
	Button
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	KnowledgeItemsFilter,
	useDataSource
} from "client/Features/Hub/KnowledgeItems/CategoryOverview/useDataSource";
import {
	DataSourceSortDirection,
	DataSourceSortableState
} from "client/Data/sorting";
import { getCategoryPageContent, KnowledgeItemPreviewItemModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItemCategoryModel, HubDataSource } from "client/Api/Hub/hub";
import {
	AlertManager,
	MediaQueryKind,
	RemoteDataSourceState,
	useAlerts,
	useDeepComparison,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import {
	ReactElement,
	useMemo,
	useState,
	useEffect,
	useCallback,
	SetStateAction,
	useRef,
	RefObject
} from "react";
import styles from "./CardOverview.module.less";
import { ActiveFilter, HubItemIsNewFilter } from "../../common";
import { HubListButton } from "../CategoryOverview/List/HubItem/HubListButton";
import {
	HubOverviewLayout,
	HubOverviewLayoutSectionContainerHandle
} from "../../HubOverviewLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubCategoryPageContent } from "../../HubCategoryPageContent/HubCategoryPageContent";
import { defaultSortingOption } from "../common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	HubEvent,
	subscribe,
	unsubscribe
} from "../../events";

const PAGE_SIZE = 24;

export type CardOverviewProps = {
	activeFilter: ActiveFilter | null;
	sorting: DataSourceSortableState<KnowledgeItemPreviewItemModel>;
	searchTerm: string;
	getCategory: (id: number | nil) => HubItemCategoryModel | undefined;
	onNavigateToItem: (id: number) => void;
	onActiveFilterChange: (filter: ActiveFilter | null) => void;
	onSelectedCategoryIdChange: (id: number) => void;
	selectedCategoryId: number | null;
	shouldReserveSpaceForFloatingActionButton: boolean;
	onScrollToTop(): void;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const CardOverview = (props: CardOverviewProps): ReactElement => {
	const {
		searchTerm,
		sorting,
		onNavigateToItem,
		getCategory,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		onScrollToTop,
		activeFilter,
		selectedCategoryId,
		shouldReserveSpaceForFloatingActionButton,
		timezone,
		isNewFilter
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [desiredAmountOfPinnedItems, setDesiredAmountOfPinnedItems] = useState<number>(PAGE_SIZE);
	const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
	const [categoryPageContent, setCategoryPageContent] = useState<string | null>(null);
	const [newStatusClearedItemIds, setNewStatusClearedItemIds] = useState<Set<number>>(new Set());

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const containerRef: RefObject<HubOverviewLayoutSectionContainerHandle | null> = useRef<HubOverviewLayoutSectionContainerHandle>(null);
	const alertManager: AlertManager = useAlerts();

	const filtering = useMemo(
		(): KnowledgeItemsFilter => {
			return {
				categoryId: selectedCategoryId,
				activeFilter,
				isPinned: false,
				isNewFilter
			};
		},
		[
			selectedCategoryId,
			activeFilter,
			isNewFilter
		]
	);

	const filtersVersion: number = useDeepComparison([
		searchTerm,
		filtering,
		sorting
	]);

	useEffect(
		() => {
			setDesiredAmountOfItems(PAGE_SIZE);
			setDesiredAmountOfPinnedItems(PAGE_SIZE);
			setIsCollapsed(true);
			setNewStatusClearedItemIds(new Set());
		},
		[filtersVersion]
	);

	useEffect(
		(): (() => void) | void => {
			async function fetch(signal: AbortSignal) {
				if (isNil(selectedCategoryId)) {
					return;
				}

				try {
					const {
						content
					} = await getCategoryPageContent(
						selectedCategoryId,
						signal
					);

					setCategoryPageContent(content);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			}

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			selectedCategoryId
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage,
		reload
	} = useDataSource({
		desiredAmountOfItems,
		filtering,
		sorting,
		searchTerm
	});

	const {
		currentItems: currentPinnedArticles,
		hasMoreOnServer: hasMorePinnedArticlesOnServer,
		getLoadingState: getPinnedLoadingState,
		hasLazyLoadError: hasPinnedArticlesLazyLoadError
	} = useDataSource({
		desiredAmountOfItems: desiredAmountOfPinnedItems,
		filtering: {
			...filtering,
			isPinned: true
		},
		sorting: selectedCategoryId
			? pinnedDateSorting
			: sorting,
		searchTerm
	});

	const [
		currentItemsToRender,
		currentPinnedArticlesToRender
	] = useMemo(
		(): [
			currentItemsToRender: readonly KnowledgeItemPreviewItemModel[],
			currentPinnedArticlesToRender: readonly KnowledgeItemPreviewItemModel[]
		] => {
			return [
				currentItems.map(
					x => newStatusClearedItemIds.has(x.id)
						? {
							...x,
							isNew: false
						}
						: x
				),
				currentPinnedArticles.map(
					x => newStatusClearedItemIds.has(x.id)
						? {
							...x,
							isNew: false
						}
						: x
				)
			];
		},
		[
			currentItems,
			currentPinnedArticles,
			newStatusClearedItemIds
		]
	);

	useEffect(
		(): (() => void) => {
			const isNewStatusClearedToken: PubSubSubscriptionToken = subscribe(
				HubEvent.IsNewStatusCleared,
				(_, data) => {
					if (data.kind !== HubDataSource.KnowledgeItem) {
						return;
					}

					setNewStatusClearedItemIds(
						viewedItemIds => {
							const newSet: Set<number> = new Set(viewedItemIds);
							return newSet.add(data.itemId);
						}
					);
				}
			);

			return (): void => unsubscribe(isNewStatusClearedToken);
		},
		[]
	);

	const isRenderingOfPinnedItemsRestricted: boolean = !searchTerm && isCollapsed;
	const hasPinnedArticles: boolean = currentPinnedArticlesToRender.length > 0;

	const loadingState: RemoteDataSourceState = getLoadingState();
	const pinnedLoadingState: RemoteDataSourceState = getPinnedLoadingState();

	const increaseAmount = useCallback(
		(
			setDesiredAmountOfItems: (value: SetStateAction<number>) => void,
			loadingState: RemoteDataSourceState,
			hasMoreOnServer: boolean,
			hasLazyLoadError: boolean
		): void => {
			const isLoading = loadingState === "loading";

			if (!isLoading
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
			}
		},
		[]
	);

	const loadMorePinnedHandler = useCallback(
		(): void => {
			increaseAmount(
				setDesiredAmountOfPinnedItems,
				pinnedLoadingState,
				hasMorePinnedArticlesOnServer,
				hasPinnedArticlesLazyLoadError
			);
		},
		[
			hasMorePinnedArticlesOnServer,
			hasPinnedArticlesLazyLoadError,
			increaseAmount,
			pinnedLoadingState
		]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			increaseAmount(
				setDesiredAmountOfItems,
				loadingState,
				hasMoreOnServer,
				hasLazyLoadError
			);
		},
		[
			hasLazyLoadError,
			hasMoreOnServer,
			increaseAmount,
			loadingState
		]
	);

	const morePinnedClickHandler = useCallback(
		(): void => {
			if (!isCollapsed) {
				if (isMobile) {
					containerRef.current
						?.scrollToTop();
				} else {
					onScrollToTop();
				}

				setIsCollapsed(true);
			} else {
				setIsCollapsed(false);
			}
		},
		[
			isMobile,
			isCollapsed,
			onScrollToTop
		]
	);

	const hasMorePinnedItems: boolean = hasMorePinnedArticlesOnServer
		&& !hasPinnedArticlesLazyLoadError
		&& !isRenderingOfPinnedItemsRestricted;

	const renderResults = (): ReactElement => {
		if (currentItemsToRender.length + currentPinnedArticlesToRender.length > 0) {
			return (
				<>
					{hasPinnedArticles && (
						<HubOverviewLayout.Section trackingId="pinned-section">
							<HubOverviewLayout.SectionTitle trackingId="pinned-section-title">
								{localize("_Pinned")}
							</HubOverviewLayout.SectionTitle>
							<List
								trackingId="knowledge-item-list-pinned"
								items={currentPinnedArticlesToRender}
								onNavigateToItem={onNavigateToItem}
								getCategory={getCategory}
								onActiveFilterChange={onActiveFilterChange}
								onSelectedCategoryIdChange={onSelectedCategoryIdChange}
								hasMoreItems={hasMorePinnedItems}
								isBusy={pinnedLoadingState === "loading"}
								onLoadmore={loadMorePinnedHandler}
								timezone={timezone}
								{...(
									searchTerm
										? {
											collapseMode: undefined
										}
										: {
											collapseMode: {
												kind: isMobile
													? "limited-items"
													: "one-row",
												isCollapsed,
												maxItemsCount: isMobile
													? DEFAULT_PINNED_ARTICLE_MOBILE_COUNT
													: MAX_PINNED_ARTICLE_IN_A_ROW_COUNT
											},
											renderMoreButton: (
												<MoreButton
													isExpanded={!isCollapsed}
													onClick={morePinnedClickHandler}
													isMobile={isMobile}
												/>
											)
										}
								)}
							/>
						</HubOverviewLayout.Section>
					)}
					{currentItemsToRender.length > 0 && (
						<HubOverviewLayout.Section trackingId="other-section">
							{hasPinnedArticles && (
								<HubOverviewLayout.SectionTitle trackingId="other-section-title">
									{localize("_Other")}
								</HubOverviewLayout.SectionTitle>
							)}
							<List
								trackingId="knowledge-item-list-other"
								items={currentItemsToRender}
								onNavigateToItem={onNavigateToItem}
								getCategory={getCategory}
								onActiveFilterChange={onActiveFilterChange}
								onSelectedCategoryIdChange={onSelectedCategoryIdChange}
								hasMoreItems={hasMoreOnServer && !hasLazyLoadError}
								onLoadmore={loadMoreHandler}
								isBusy={loadingState === "loading"}
								timezone={timezone}
							/>
							{hasLazyLoadError && (
								<NoResults
									trackingId="has-lazy-loading-error"
									icon="gear"
									title={localize("_NotAbleToRetrieveKnowledgeArticles")}
									message={localize("_WeWereUnableToRetrieveYourKnowledgeArticles")}
									onRetry={retryToLoadLastPage}
									hasExtraTopPadding
								/>
							)}
						</HubOverviewLayout.Section>
					)}
					{shouldReserveSpaceForFloatingActionButton && (
						<HubOverviewLayout.MobilePlusMenuSpacing />
					)}
				</>
			);
		}

		if (loadingState === "loading" || pinnedLoadingState === "loading") {
			return <BusyIndicator />;
		}

		if (loadingState === "error") {
			return (
				<NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_NotAbleToRetrieveKnowledgeArticles")}
					message={localize("_WeWereUnableToRetrieveYourKnowledgeArticles")}
					onRetry={reload}
					height="fill"
				/>
			);
		}

		if (searchTerm) {
			return (
				<NoResults
					trackingId="no-search-results"
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
				/>
			);
		}

		return (
			<NoResults
				trackingId="no-knowledge-items-yet"
				icon="book"
				title={localize("_NoKnowledgeArticlesYet")}
				message={getNoKnowledgeItemsMessage(activeFilter, selectedCategoryId)}
				height="fill"
			/>
		);
	};

	const hasAnyFilter: boolean = useMemo(
		(): boolean => {
			return searchTerm !== ""
				|| isNotNil(activeFilter)
				|| !shallowComparison(sorting, defaultSortingOption.value);
		},
		[
			activeFilter,
			searchTerm,
			sorting
		]
	);

	return (
		<>
			{(categoryPageContent && !hasAnyFilter) && (
				<div className={styles.pageContent}>
					<HubCategoryPageContent
						text={categoryPageContent}
						trackingId="category-page-content"
					/>
				</div>
			)}
			{renderResults()}
		</>
	);
};

CardOverview.displayName = "CardOverview";

const DEFAULT_PINNED_ARTICLE_MOBILE_COUNT = 2;
const MAX_PINNED_ARTICLE_IN_A_ROW_COUNT = 10;

type MoreButtonProps = {
	isExpanded: boolean;
	onClick(): void;
	isMobile?: boolean;
};

const MoreButton = (props: MoreButtonProps): ReactElement => {
	const {
		isExpanded,
		onClick,
		isMobile = false
	} = props;

	const text: string = isExpanded
		? localize("_ShowLessPinnedArticles")
		: localize("_ShowMorePinnedArticles");

	if (isMobile) {
		return (
			<HubListButton
				onClick={onClick}
				trackingId="more-pinned-items-mobile"
			>
				{text}
			</HubListButton>
		);
	}

	return (
		<div className={styles.moreButtonContainer}>
			<Button
				kind="transparent"
				icon={isExpanded
					? "chevron-up"
					: "chevron-down"
				}
				onClick={onClick}
				text={text}
				trackingId="more-pinned-items"
			/>
		</div>
	);
};
MoreButton.displayName = "MoreButton";

const pinnedDateSorting: DataSourceSortableState<KnowledgeItemPreviewItemModel> = {
	direction: DataSourceSortDirection.Descending,
	propertyName: "pinDate"
};

function getNoKnowledgeItemsMessage(
	filter: ActiveFilter | null,
	categoryFilter: number | null
): string {
	let keySelection: Record<0 | 1, LocalizationKey>;
	if (filter?.kind === "author") {
		keySelection = {
			[0]: "_TheAuthorDidNotPublishAnyItemsYet",
			[1]: "_TheAuthorDidNotPublishAnyItemsInThisCategoryYet"
		};
	} else if (filter?.kind === "tag") {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsWithThisTag",
			[1]: "_ThereAreNoPublishedItemsWithThisTagInThisCategory"
		};
	} else {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsYet",
			[1]: "_ThereAreNoPublishedItemsInThisCategoryYet"
		};
	}

	return localize(
		keySelection[isNil(categoryFilter) ? 0 : 1]
	);
}
